import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FabricGrade } from '../../interfaces/stock.interface';

export interface FabricAttributes {
  dyeLot?: string;
  shade?: string;
  widthCm?: number;
  gsm?: number;
  rollCount?: number;
  supplierBatchRef?: string;
  grade?: FabricGrade;
  notes?: string;
}

interface FabricAttributeFieldsProps {
  value: FabricAttributes;
  onChange: (next: FabricAttributes) => void;
  disabled?: boolean;
  idPrefix: string;
}

const FABRIC_GRADES: FabricGrade[] = ['A', 'B', 'C', 'REJECT'];

const FabricAttributeFields: React.FC<FabricAttributeFieldsProps> = ({ value, onChange, disabled, idPrefix }) => {
  const setField = (field: keyof FabricAttributes, fieldValue: FabricAttributes[keyof FabricAttributes]) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleNumberChange = (field: 'widthCm' | 'gsm' | 'rollCount') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setField(field, raw === '' ? undefined : Number(raw));
    };

  const handleTextChange = (field: 'dyeLot' | 'shade' | 'supplierBatchRef' | 'notes') =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setField(field, e.target.value === '' ? undefined : e.target.value);
    };

  const handleGradeChange = (e: SelectChangeEvent) => {
    setField('grade', e.target.value === '' ? undefined : (e.target.value as FabricGrade));
  };

  return (
    <Accordion disableGutters data-testid={`${idPrefix}-fabric-accordion`} data-help-section="common.fabric.section">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>Fabric details (optional)</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            label="Dye Lot"
            data-help="common.fabric.field.dyeLot"
            value={value.dyeLot ?? ''}
            onChange={handleTextChange('dyeLot')}
            disabled={disabled}
            sx={{ flex: '1 1 200px', maxWidth: 280 }}
            data-testid={`${idPrefix}-dye-lot`}
          />
          <TextField
            label="Shade"
            data-help="common.fabric.field.shade"
            value={value.shade ?? ''}
            onChange={handleTextChange('shade')}
            disabled={disabled}
            sx={{ flex: '1 1 200px', maxWidth: 280 }}
            data-testid={`${idPrefix}-shade`}
          />
          <FormControl sx={{ flex: '1 1 200px', maxWidth: 280 }} disabled={disabled} data-help="common.fabric.field.grade">
            <InputLabel id={`${idPrefix}-grade-label`}>Grade</InputLabel>
            <Select
              labelId={`${idPrefix}-grade-label`}
              label="Grade"
              value={value.grade ?? ''}
              onChange={handleGradeChange}
              data-testid={`${idPrefix}-grade`}
            >
              <MenuItem value="">—</MenuItem>
              {FABRIC_GRADES.map(g => (
                <MenuItem key={g} value={g}>{g}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Width (cm)"
            data-help="common.fabric.field.widthCm"
            type="number"
            value={value.widthCm ?? ''}
            onChange={handleNumberChange('widthCm')}
            disabled={disabled}
            sx={{ flex: '1 1 200px', maxWidth: 280 }}
            inputProps={{ min: 0, max: 9999.99, step: 'any' }}
            data-testid={`${idPrefix}-width-cm`}
          />
          <TextField
            label="GSM"
            data-help="common.fabric.field.gsm"
            type="number"
            value={value.gsm ?? ''}
            onChange={handleNumberChange('gsm')}
            disabled={disabled}
            sx={{ flex: '1 1 200px', maxWidth: 280 }}
            inputProps={{ min: 0, max: 9999.99, step: 'any' }}
            data-testid={`${idPrefix}-gsm`}
          />
          <TextField
            label="Rolls"
            data-help="common.fabric.field.rolls"
            type="number"
            value={value.rollCount ?? ''}
            onChange={handleNumberChange('rollCount')}
            disabled={disabled}
            sx={{ flex: '1 1 200px', maxWidth: 280 }}
            inputProps={{ min: 0, max: 100000, step: 1 }}
            data-testid={`${idPrefix}-roll-count`}
          />
          <TextField
            label="Supplier Batch"
            data-help="common.fabric.field.supplierBatch"
            value={value.supplierBatchRef ?? ''}
            onChange={handleTextChange('supplierBatchRef')}
            disabled={disabled}
            sx={{ flex: '1 1 200px', maxWidth: 280 }}
            data-testid={`${idPrefix}-supplier-batch`}
          />
          <TextField
            label="Notes"
            data-help="common.fabric.field.notes"
            value={value.notes ?? ''}
            onChange={handleTextChange('notes')}
            disabled={disabled}
            multiline
            rows={2}
            fullWidth
            sx={{ flex: '1 1 100%' }}
            data-testid={`${idPrefix}-notes`}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default FabricAttributeFields;
